import { Directive, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ReuseTabService } from '@delon/abc/reuse-tab';
import { NzMessageService } from 'ng-zorro-antd/message';

@Directive({
  selector: '[appKeyboardShortcuts]',
  standalone: true
})
export class KeyboardShortcutsDirective implements OnInit, OnDestroy {
  private eventListener: (event: KeyboardEvent) => void;

  constructor(
    private readonly reuseTabService: ReuseTabService,
    private readonly router: Router,
    private readonly messageService: NzMessageService
  ) {
    // Create a bound event listener
    this.eventListener = this.handleKeyDown.bind(this);
  }

  ngOnInit() {
    // Add event listener with capture phase to intercept before browser
    document.addEventListener('keydown', this.eventListener, true);
  }

  ngOnDestroy() {
    // Clean up event listeners
    document.removeEventListener('keydown', this.eventListener, true);
  }

  private handleKeyDown(event: KeyboardEvent): void {

    // Check if we have tabs to work with
    const tabs = this.reuseTabService.items;
    if (tabs.length <= 1) {
      return; // No need to handle shortcuts if there's only one tab
    }

    // Check if we're in an input field or textarea (don't interfere with typing)
    const target = event.target as HTMLElement;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.contentEditable === 'true')) {
      return;
    }

    // Handle Alt+Shift+ArrowRight to go to next tab
    if (event.altKey && event.ctrlKey && event.key.toLowerCase() === 'arrowright') {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.goToNextTab();
      return;
    }

    // Handle Alt+Shift+ArrowLeft to go to previous tab
    if (event.altKey && event.ctrlKey && event.key.toLowerCase() === 'arrowleft') {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.goToPreviousTab();
      return;
    }

    // Handle Alt+W to close current tab
    if (event.altKey && event.key.toLowerCase() === 'w') {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.closeCurrentTab();
      return;
    }

    // Handle Alt+1-9 to switch to specific tab
    if (event.altKey && /^[1-9]$/.test(event.key)) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      const tabIndex = parseInt(event.key) - 1;
      this.switchToTab(tabIndex);
      return;
    }
  }

  private goToNextTab(): void {
    const currentIndex = this.reuseTabService.items.findIndex(tab => tab.url === this.reuseTabService.curUrl);
    const nextIndex = (currentIndex + 1) % this.reuseTabService.items.length;
    this.switchToTab(nextIndex);
  }

  private goToPreviousTab(): void {
    const currentIndex = this.reuseTabService.items.findIndex(tab => tab.url === this.reuseTabService.curUrl);
    const previousIndex = (currentIndex - 1 + this.reuseTabService.items.length) % this.reuseTabService.items.length;
    this.switchToTab(previousIndex);
  }

  private switchToTab(index: number): void {
    const tabs = this.reuseTabService.items;
    if (index < 0 || index >= tabs.length) {
      this.showMessage(`Tab ${index + 1} does not exist`, 'warning');
      return;
    }

    const targetTab = tabs[index];
    // Use router for proper navigation
    this.router.navigateByUrl(targetTab.url);
  }

  private closeCurrentTab(): void {
    const currentUrl = this.reuseTabService.curUrl;
    const tabs = this.reuseTabService.items;
    
    if (tabs.length <= 1) {
      this.showMessage('Cannot close the last tab', 'warning');
      return;
    }
    
    const currentIndex = tabs.findIndex(tab => tab.url === currentUrl);
    const currentTabTitle = typeof tabs[currentIndex].title === 'string' ? tabs[currentIndex].title : 'Tab';
    
    this.reuseTabService.close(currentUrl);
    
    // Navigate to the next available tab
    const remainingTabs = this.reuseTabService.items;
    if (remainingTabs.length > 0) {
      const nextIndex = Math.min(currentIndex, remainingTabs.length - 1);
      this.router.navigateByUrl(remainingTabs[nextIndex].url);
    }
  }

  private showMessage(message: string, type: 'success' | 'info' | 'warning' | 'error' = 'info'): void {
    this.messageService.create(type, message, {
      nzDuration: 1500,
      nzPauseOnHover: false
    });
  }
}
